package se.mittpass.web.components

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*


import androidx.compose.runtime.*
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import se.mittpass.shared.api.FeedbackRequest
import se.mittpass.shared.api.RegisterRequest
import se.mittpass.shared.repository.FeedbackRepository

// ─────────────────────────────────────────
// HERO
// ─────────────────────────────────────────
@Composable
fun LandingHero(onLogin: () -> Unit, onSignUp: () -> Unit = {}, onDemoLogin: () -> Unit = {}) {
    Section({
        id("hero")
        style {
            property("padding", "6rem 1.75rem 5rem")
            property("background", "var(--rp-surface)")
            property("position", "relative")
            property("overflow", "hidden")
        }
    }) {
        // Dot-grid decorative panel (right side, clipped)
        Div({
            classes("dot-grid-bg")
            style {
                property("position", "absolute")
                property("top", "0")
                property("right", "0")
                property("width", "45%")
                property("bottom", "0")
                property("pointer-events", "none")
                property("opacity", "0.5")
            }
        })

        // Soft gradient blob
        Div({
            style {
                property("position", "absolute")
                property("top", "-120px")
                property("right", "5%")
                property("width", "480px")
                property("height", "480px")
                property("border-radius", "50%")
                property("background", "radial-gradient(circle, rgba(44,63,168,0.08) 0%, transparent 65%)")
                property("pointer-events", "none")
            }
        })

        Div({
            style {
                property("max-width", "1200px")
                property("margin", "0 auto")
                property("position", "relative")
                property("z-index", "1")
            }
        }) {
            var activeTab by remember { mutableStateOf("all") }
            var isCheckedIn by remember { mutableStateOf(false) }
            var extraStaffAdded by remember { mutableStateOf(false) }
            var tempKyl by remember { mutableStateOf(4.2) }
            var tempFrys by remember { mutableStateOf(-18.5) }

            val eyebrowText: String
            val headlinePart1: String
            val headlinePart2: String
            val subheadText: String

            val dict = LocaleManager.dictionary
            when (activeTab) {
                "hr" -> {
                    eyebrowText = dict.simTabHrEyebrow
                    headlinePart1 = dict.simTabHrHeadline1
                    headlinePart2 = dict.simTabHrHeadline2
                    subheadText = dict.simTabHrSubhead
                }
                "liggare" -> {
                    eyebrowText = dict.simTabLiggareEyebrow
                    headlinePart1 = dict.simTabLiggareHeadline1
                    headlinePart2 = dict.simTabLiggareHeadline2
                    subheadText = dict.simTabLiggareSubhead
                }
                "haccp" -> {
                    eyebrowText = dict.simTabHaccpEyebrow
                    headlinePart1 = dict.simTabHaccpHeadline1
                    headlinePart2 = dict.simTabHaccpHeadline2
                    subheadText = dict.simTabHaccpSubhead
                }
                else -> {
                    eyebrowText = dict.simTabDefaultEyebrow
                    headlinePart1 = dict.simTabDefaultHeadline1
                    headlinePart2 = dict.simTabDefaultHeadline2
                    subheadText = dict.simTabDefaultSubhead
                }
            }

            Div({
                classes("landing-hero-grid")
                style {
                    property("display", "grid")
                    property("grid-template-columns", "repeat(auto-fit, minmax(min(100%, 300px), 1fr))")
                    property("gap", "2.5rem")
                    property("align-items", "center")
                }
            }) {
                // Left Column: Text Content
                Div({
                    style {
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("align-items", "flex-start")
                        property("min-width", "0")
                    }
                }) {
                    // Tab Selector
                    Div({
                        classes("fade-up", "fade-up-1", "no-scrollbar")
                        style {
                            property("display", "inline-flex")
                            property("background", "var(--rp-overlay)")
                            property("padding", "0.25rem")
                            property("border-radius", "30px")
                            property("margin-bottom", "1.5rem")
                            property("gap", "0.25rem")
                            property("max-width", "100%")
                            property("overflow-x", "auto")
                        }
                    }) {
                        val tabs = listOf(
                            "all" to "Allt-i-ett",
                            "hr" to "Personalhantering",
                            "liggare" to "Personalliggare",
                            "haccp" to "Livsmedelskontroll"
                        )
                        tabs.forEach { (key, label) ->
                            val isActive = activeTab == key
                            Button({
                                onClick { activeTab = key }
                                style {
                                    property("padding", "0.5rem 1.25rem")
                                    property("border-radius", "25px")
                                    property("border", "none")
                                    property("font-family", "inherit")
                                    property("font-size", "0.85rem")
                                    property("font-weight", "600")
                                    property("cursor", "pointer")
                                    property("white-space", "nowrap")
                                    property("flex-shrink", "0")
                                    if (isActive) {
                                        property("background", "var(--rp-pine)")
                                        property("color", "white")
                                    } else {
                                        property("background", "transparent")
                                        property("color", "var(--rp-subtle)")
                                    }
                                    property("transition", "all 0.2s ease")
                                }
                            }) {
                                Text(label)
                            }
                        }
                    }

                    // Eyebrow pill
                    Span({
                        classes("fade-up", "fade-up-1")
                        style {
                            property("display", "inline-flex")
                            property("align-items", "center")
                            property("gap", "0.5rem")
                            property("background", "var(--rp-info-bg)")
                            property("color", "var(--rp-pine)")
                            property("font-size", "0.8rem")
                            property("font-weight", "700")
                            property("padding", "0.4rem 1rem")
                            property("border-radius", "6px")
                            property("border", "1px solid var(--rp-info-border)")
                            property("margin-bottom", "1.5rem")
                            property("letter-spacing", "0.04em")
                            property("text-transform", "uppercase")
                        }
                    }) {
                        Span({
                            style {
                                property("width", "6px")
                                property("height", "6px")
                                property("border-radius", "50%")
                                property("background", "var(--rp-pine)")
                                property("display", "inline-block")
                            }
                        })
                        Text(eyebrowText)
                    }

                    // Main headline
                    H1({
                        classes("fade-up", "fade-up-2")
                        style {
                            property("font-family", "'Plus Jakarta Sans', system-ui, sans-serif")
                            property("font-size", "clamp(2.4rem, 4.8vw, 3.6rem)")
                            property("font-weight", "800")
                            property("color", "var(--rp-text)")
                            property("margin", "0 0 1.5rem 0")
                            property("line-height", "1.12")
                            property("letter-spacing", "-0.03em")
                        }
                    }) {
                        Text(headlinePart1)
                        Span({
                            style { property("color", "var(--rp-pine)") }
                        }) { Text(headlinePart2) }
                    }

                    // Subheadline
                    P({
                        classes("fade-up", "fade-up-3")
                        style {
                            property("font-size", "clamp(1.05rem, 1.9vw, 1.25rem)")
                            property("color", "var(--rp-subtle)")
                            property("line-height", "1.7")
                            property("margin", "0 0 2.25rem 0")
                        }
                    }) {
                        Text("Stämpla tider i mobilen, håll Skatteverket nöjda och logga HACCP-temperaturer på sekunder. Byggt för svenska caféer, pizzerior och krogar.")
                    }

                    // CTA row
                    Div({
                        classes("fade-up", "fade-up-4")
                        style {
                            property("display", "flex")
                            property("gap", "0.875rem")
                            property("align-items", "center")
                            property("flex-wrap", "wrap")
                            property("margin-bottom", "1.5rem")
                        }
                    }) {
                        Button({
                            onClick { onSignUp() }
                            style {
                                property("display", "inline-flex")
                                property("align-items", "center")
                                property("gap", "0.6rem")
                                property("background", "var(--rp-cta-bg)")
                                property("color", "var(--rp-cta-text)")
                                property("padding", "0.95rem 2rem")
                                property("border-radius", "8px")
                                property("font-size", "1.05rem")
                                property("font-weight", "700")
                                property("font-family", "inherit")
                                property("border", "none")
                                property("cursor", "pointer")
                                property("box-shadow", "0 6px 20px rgba(40,105,131,0.28)")
                                property("transition", "all 0.2s ease")
                            }
                        }) {
                            Text("Testa gratis i 30 dagar")
                            FaIcon("fa-solid fa-arrow-right", size = "0.85rem")
                        }

                        A(href = "https://play.google.com/store/apps/details?id=se.mittpass.app&hl=en", attrs = {
                            target(ATarget.Blank)
                            style {
                                property("display", "inline-flex")
                                property("align-items", "center")
                                property("gap", "0.55rem")
                                property("background", "var(--rp-surface)")
                                property("color", "var(--rp-text)")
                                property("border", "1.5px solid var(--rp-hl-med)")
                                property("padding", "0.9rem 1.4rem")
                                property("border-radius", "8px")
                                property("font-size", "0.95rem")
                                property("font-weight", "700")
                                property("text-decoration", "none")
                                property("transition", "all 0.2s ease")
                            }
                        }) {
                            FaIcon("fa-brands fa-google-play", size = "1.05rem", color = "var(--rp-pine)")
                            Text("Google Play")
                        }
                    }

                    // Trust badges under Hero
                    Div({
                        classes("fade-up", "fade-up-4")
                        style {
                            property("display", "flex")
                            property("gap", "1.25rem")
                            property("flex-wrap", "wrap")
                        }
                    }) {
                        Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.45rem"); property("color", "var(--rp-subtle)"); property("font-size", "0.85rem"); property("font-weight", "600") } }) {
                            FaIcon("fa-solid fa-shield-check", size = "0.85rem", color = "var(--rp-pine)")
                            Text("Skatteverket-godkänd")
                        }
                        Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.45rem"); property("color", "var(--rp-subtle)"); property("font-size", "0.85rem"); property("font-weight", "600") } }) {
                            FaIcon("fa-solid fa-credit-card", size = "0.85rem", color = "var(--rp-pine)")
                            Text("Inget kontokort krävs")
                        }
                        Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.45rem"); property("color", "var(--rp-subtle)"); property("font-size", "0.85rem"); property("font-weight", "600") } }) {
                            FaIcon("fa-solid fa-file-invoice", size = "0.85rem", color = "var(--rp-pine)")
                            Text("Färdigt löneunderlag")
                        }
                    }
                }

                // Right Column: Interactive UI Sandbox with 3D Tilt
                Div({
                    classes("fade-up", "fade-up-3", "tilt-card")
                    style {
                        property("display", "flex")
                        property("justify-content", "center")
                        property("align-items", "center")
                        property("position", "relative")
                        property("width", "100%")
                        property("min-width", "0")
                        property("perspective", "1000px")
                    }
                    onMouseMove { ev ->
                        val el = ev.currentTarget as? org.w3c.dom.HTMLElement ?: return@onMouseMove
                        val rect = el.getBoundingClientRect()
                        val x = ev.clientX - rect.left - (rect.width / 2)
                        val y = ev.clientY - rect.top - (rect.height / 2)
                        val rotateX = (-y / 20).coerceIn(-12.0, 12.0)
                        val rotateY = (x / 20).coerceIn(-12.0, 12.0)
                        el.style.transform = "perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)"
                    }
                    onMouseLeave { ev ->
                        val el = ev.currentTarget as? org.w3c.dom.HTMLElement ?: return@onMouseLeave
                        el.style.transform = "perspective(1000px) rotateX(0deg) rotateY(0deg)"
                    }
                }) {
                    Div({
                        style {
                            property("background", "var(--rp-surface)")
                            property("border", "1px solid var(--rp-hl-med)")
                            property("border-radius", "24px")
                            property("padding", "2rem")
                            property("box-shadow", "0 20px 40px rgba(44, 63, 168, 0.08)")
                            property("width", "100%")
                            property("max-width", "420px")
                            property("min-height", "320px")
                            property("position", "relative")
                            property("color", "var(--rp-text)")
                            property("font-family", "inherit")
                            property("display", "flex")
                            property("flex-direction", "column")
                            property("justify-content", "space-between")
                        }
                    }) {
                        when (activeTab) {
                            "hr" -> {
                                // Personalhantering Simulator
                                Div {
                                    val dict = LocaleManager.dictionary
                                    H3({
                                        style {
                                            property("margin", "0 0 1.25rem 0")
                                            property("font-size", "1.2rem")
                                            property("font-weight", "700")
                                            property("color", "var(--rp-text)")
                                        }
                                    }) { Text(dict.simDagensSchema) }

                                    // Simulated shifts
                                    listOf(
                                        "08:00 - 16:00" to "Emma Larsson (${dict.simRoleServering})",
                                        "12:00 - 20:00" to "Johan Berg (${dict.simRoleKok})",
                                        "16:00 - 22:00" to "Nils Ek (${dict.simRoleServering})"
                                    ).forEach { (time, name) ->
                                        Div({
                                            style {
                                                property("display", "flex")
                                                property("justify-content", "space-between")
                                                property("align-items", "center")
                                                property("padding", "0.75rem 1rem")
                                                property("background", "var(--rp-overlay)")
                                                property("border", "1px solid var(--rp-hl-med)")
                                                property("border-radius", "12px")
                                                property("margin-bottom", "0.5rem")
                                            }
                                        }) {
                                            Span({ style { property("color", "var(--rp-pine)"); property("font-weight", "700"); property("font-size", "0.85rem") } }) { Text(time) }
                                            Span({ style { property("color", "var(--rp-text)"); property("font-size", "0.85rem"); property("font-weight", "600") } }) { Text(name) }
                                        }
                                    }

                                    if (extraStaffAdded) {
                                        Div({
                                            style {
                                                property("display", "flex")
                                                property("align-items", "center")
                                                property("gap", "0.5rem")
                                                property("padding", "0.75rem 1rem")
                                                property("background", "rgba(16, 185, 129, 0.1)")
                                                property("border", "1px solid rgba(16, 185, 129, 0.3)")
                                                property("border-radius", "12px")
                                                property("margin-top", "0.5rem")
                                                property("color", "var(--rp-ok-text)")
                                                property("font-size", "0.85rem")
                                                property("font-weight", "600")
                                            }
                                        }) {
                                            I({ classes("fa-solid", "fa-circle-check") })
                                            Text(dict.simExtraStaffAddedText)
                                        }
                                    }
                                }

                                Button({
                                    onClick { extraStaffAdded = !extraStaffAdded }
                                    style {
                                        property("width", "100%")
                                        property("padding", "0.8rem")
                                        property("border-radius", "12px")
                                        property("border", "none")
                                        property("background", if (extraStaffAdded) "var(--rp-overlay)" else "var(--rp-pine)")
                                        property("color", if (extraStaffAdded) "var(--rp-subtle)" else "white")
                                        property("font-weight", "700")
                                        property("font-family", "inherit")
                                        property("cursor", "pointer")
                                        property("margin-top", "1rem")
                                        property("transition", "all 0.2s ease")
                                    }
                                }) {
                                    val dict = LocaleManager.dictionary
                                    Text(if (extraStaffAdded) dict.simResetBtn else dict.simInviteStaffBtn)
                                }
                            }
                            "liggare" -> {
                                // Personalliggare Simulator
                                Div {
                                    val dict = LocaleManager.dictionary
                                    H3({
                                        style {
                                            property("margin", "0 0 1.25rem 0")
                                            property("font-size", "1.2rem")
                                            property("font-weight", "700")
                                            property("color", "var(--rp-text)")
                                        }
                                    }) { Text(dict.simSkatteverketLiggare) }

                                    Div({
                                        style {
                                            property("text-align", "center")
                                            property("padding", "2rem 1rem")
                                            property("background", "var(--rp-overlay)")
                                            property("border-radius", "16px")
                                            property("border", "1px dashed var(--rp-hl-med)")
                                        }
                                    }) {
                                        Div({
                                            style {
                                                property("display", "inline-flex")
                                                property("align-items", "center")
                                                property("gap", "0.5rem")
                                                property("padding", "0.4rem 1rem")
                                                property("border-radius", "30px")
                                                property("background", if (isCheckedIn) "rgba(16, 185, 129, 0.1)" else "var(--rp-overlay)")
                                                property("color", if (isCheckedIn) "var(--rp-ok-text)" else "var(--rp-subtle)")
                                                property("font-size", "0.85rem")
                                                property("font-weight", "700")
                                                property("margin-bottom", "1rem")
                                            }
                                        }) {
                                            Span({
                                                style {
                                                    property("width", "8px")
                                                    property("height", "8px")
                                                    property("border-radius", "50%")
                                                    property("background", if (isCheckedIn) "var(--rp-pine)" else "var(--rp-subtle)")
                                                    if (isCheckedIn) {
                                                        property("box-shadow", "0 0 8px var(--rp-ok-text)")
                                                    }
                                                }
                                            })
                                            Text(if (isCheckedIn) dict.simCheckedInStatus else dict.simNotCheckedInStatus)
                                        }

                                        P({
                                            style {
                                                property("margin", "0")
                                                property("font-size", "0.85rem")
                                                property("color", "var(--rp-subtle)")
                                            }
                                        }) {
                                            Text(if (isCheckedIn) dict.simCheckedInDesc else dict.simNotCheckedInDesc)
                                        }
                                    }
                                }

                                Button({
                                    onClick { isCheckedIn = !isCheckedIn }
                                    style {
                                        property("width", "100%")
                                        property("padding", "0.8rem")
                                        property("border-radius", "12px")
                                        property("border", "none")
                                        property("background", if (isCheckedIn) "var(--rp-love)" else "var(--rp-pine)")
                                        property("color", "white")
                                        property("font-weight", "700")
                                        property("font-family", "inherit")
                                        property("cursor", "pointer")
                                        property("margin-top", "1rem")
                                        property("transition", "all 0.2s ease")
                                    }
                                }) {
                                    val dict = LocaleManager.dictionary
                                    Text(if (isCheckedIn) dict.simCheckOutBtn else dict.simCheckInBtn)
                                }
                            }
                            "haccp" -> {
                                // Livsmedelskontroll Simulator
                                Div {
                                    val dict = LocaleManager.dictionary
                                    H3({
                                        style {
                                            property("margin", "0 0 1.25rem 0")
                                            property("font-size", "1.2rem")
                                            property("font-weight", "700")
                                            property("color", "var(--rp-text)")
                                        }
                                    }) { Text(dict.simHaccpTitle) }

                                    // Custom input rows
                                    Div({
                                        style {
                                            property("display", "flex")
                                            property("flex-direction", "column")
                                            property("gap", "0.75rem")
                                        }
                                    }) {
                                        // Row 1
                                        Div({
                                            style {
                                                property("display", "flex")
                                                property("justify-content", "space-between")
                                                property("align-items", "center")
                                                property("padding", "0.75rem 1rem")
                                                property("background", "var(--rp-overlay)")
                                                property("border", "1px solid var(--rp-hl-med)")
                                                property("border-radius", "12px")
                                            }
                                        }) {
                                            Span({ style { property("font-size", "0.85rem"); property("color", "var(--rp-text)"); property("font-weight", "600") } }) { Text(dict.simKyl1Name) }
                                            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem") } }) {
                                                Input(InputType.Number) {
                                                    value(tempKyl.toString())
                                                    onInput { event ->
                                                        (event.target.asDynamic().value as? String)?.toDoubleOrNull()?.let { tempKyl = it }
                                                    }
                                                    style {
                                                        property("width", "60px")
                                                        property("background", "var(--rp-surface)")
                                                        property("border", "1.5px solid var(--rp-hl-med)")
                                                        property("color", "var(--rp-text)")
                                                        property("border-radius", "6px")
                                                        property("padding", "0.2rem 0.4rem")
                                                        property("text-align", "center")
                                                        property("font-size", "0.85rem")
                                                        property("font-weight", "600")
                                                    }
                                                }
                                                Span({ style { property("font-size", "0.85rem"); property("color", "var(--rp-subtle)") } }) { Text("°C") }
                                                I({
                                                    classes("fa-solid", "fa-circle-check")
                                                    style { property("color", if (tempKyl in 2.0..8.0) "var(--rp-ok-text)" else "var(--rp-err-text)") }
                                                })
                                            }
                                        }

                                        // Row 2
                                        Div({
                                            style {
                                                property("display", "flex")
                                                property("justify-content", "space-between")
                                                property("align-items", "center")
                                                property("padding", "0.75rem 1rem")
                                                property("background", "var(--rp-overlay)")
                                                property("border", "1px solid var(--rp-hl-med)")
                                                property("border-radius", "12px")
                                            }
                                        }) {
                                            Span({ style { property("font-size", "0.85rem"); property("color", "var(--rp-text)"); property("font-weight", "600") } }) { Text(dict.simFrysName) }
                                            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem") } }) {
                                                Input(InputType.Number) {
                                                    value(tempFrys.toString())
                                                    onInput { event ->
                                                        (event.target.asDynamic().value as? String)?.toDoubleOrNull()?.let { tempFrys = it }
                                                    }
                                                    style {
                                                        property("width", "60px")
                                                        property("background", "var(--rp-surface)")
                                                        property("border", "1.5px solid var(--rp-hl-med)")
                                                        property("color", "var(--rp-text)")
                                                        property("border-radius", "6px")
                                                        property("padding", "0.2rem 0.4rem")
                                                        property("text-align", "center")
                                                        property("font-size", "0.85rem")
                                                        property("font-weight", "600")
                                                    }
                                                }
                                                Span({ style { property("font-size", "0.85rem"); property("color", "var(--rp-subtle)") } }) { Text("°C") }
                                                I({
                                                    classes("fa-solid", "fa-circle-check")
                                                    style { property("color", if (tempFrys <= -15.0) "var(--rp-ok-text)" else "var(--rp-err-text)") }
                                                })
                                            }
                                        }
                                    }
                                }

                                Div({
                                    style {
                                        property("font-size", "0.78rem")
                                        property("color", if (tempKyl in 2.0..8.0 && tempFrys <= -15.0) "var(--rp-ok-text)" else "var(--rp-err-text)")
                                        property("text-align", "center")
                                        property("margin-top", "1rem")
                                        property("font-weight", "700")
                                    }
                                }) {
                                    val dict = LocaleManager.dictionary
                                    Text(if (tempKyl in 2.0..8.0 && tempFrys <= -15.0) dict.simAllTempsOk else dict.simTempsWarning)
                                }
                            }
                            else -> {
                                 // Allt-i-ett Dashboard Overview
                                 Div {
                                     val dict = LocaleManager.dictionary
                                     H3({
                                         style {
                                             property("margin", "0 0 1.25rem 0")
                                             property("font-size", "1.2rem")
                                             property("font-weight", "700")
                                             property("color", "var(--rp-text)")
                                         }
                                     }) { Text(dict.simRealtimeOps) }

                                     Div({
                                         style {
                                             property("display", "flex")
                                             property("flex-direction", "column")
                                             property("gap", "0.75rem")
                                         }
                                     }) {
                                         // Schema row
                                         Div({
                                             style {
                                                 property("display", "flex")
                                                 property("align-items", "center")
                                                 property("gap", "0.75rem")
                                                 property("padding", "0.6rem 0.85rem")
                                                 property("background", "var(--rp-overlay)")
                                                 property("border", "1px solid var(--rp-hl-med)")
                                                 property("border-radius", "12px")
                                             }
                                         }) {
                                             I({ classes("fa-solid", "fa-calendar-day"); style { property("color", "var(--rp-pine)"); property("font-size", "1.1rem") } })
                                             Div {
                                                 Div({ style { property("font-weight", "700"); property("font-size", "0.85rem"); property("color", "var(--rp-text)") } }) { Text(dict.simScheduleAndShifts) }
                                                 Div({ style { property("font-size", "0.75rem"); property("color", "var(--rp-subtle)") } }) { Text(dict.simActiveShiftsToday) }
                                             }
                                         }

                                         // Personalliggare status row
                                         Div({
                                             style {
                                                 property("display", "flex")
                                                 property("align-items", "center")
                                                 property("gap", "0.75rem")
                                                 property("padding", "0.6rem 0.85rem")
                                                 property("background", "var(--rp-overlay)")
                                                 property("border", "1px solid var(--rp-hl-med)")
                                                 property("border-radius", "12px")
                                             }
                                         }) {
                                             I({ classes("fa-solid", "fa-clipboard-user"); style { property("color", "var(--rp-ok-text)"); property("font-size", "1.1rem") } })
                                             Div {
                                                 Div({ style { property("font-weight", "700"); property("font-size", "0.85rem"); property("color", "var(--rp-text)") } }) { Text(dict.personalliggare) }
                                                 Div({ style { property("font-size", "0.75rem"); property("color", "var(--rp-subtle)") } }) { Text(dict.simSkatteverketAuditProof) }
                                             }
                                         }

                                         // HACCP status row
                                         Div({
                                             style {
                                                 property("display", "flex")
                                                 property("align-items", "center")
                                                 property("gap", "0.75rem")
                                                 property("padding", "0.6rem 0.85rem")
                                                 property("background", "var(--rp-overlay)")
                                                 property("border", "1px solid var(--rp-hl-med)")
                                                 property("border-radius", "12px")
                                             }
                                         }) {
                                             I({ classes("fa-solid", "fa-temperature-half"); style { property("color", "var(--rp-pine)"); property("font-size", "1.1rem") } })
                                             Div {
                                                 Div({ style { property("font-weight", "700"); property("font-size", "0.85rem"); property("color", "var(--rp-text)") } }) { Text(dict.simFoodSafetyTitle) }
                                                 Div({ style { property("font-size", "0.75rem"); property("color", "var(--rp-subtle)") } }) { Text(dict.simHygieneRounds) }
                                             }
                                         }
                                     }
                                 }

                                 Div({
                                     style {
                                         property("font-size", "0.75rem")
                                         property("color", "var(--rp-pine)")
                                         property("text-align", "center")
                                         property("margin-top", "1rem")
                                         property("font-weight", "700")
                                     }
                                 }) {
                                     Text(LocaleManager.dictionary.simClickTabsToTest)
                                 }
                            }
                        }
                    }
                }
            }
        }
    }
}

// ─────────────────────────────────────────
// TRUST & COMPLIANCE BAR
// ─────────────────────────────────────────
@Composable
fun LandingTrustBar() {
    Section({
        style {
            property("background", "var(--rp-overlay)")
            property("border-top", "1px solid var(--rp-hl-med)")
            property("border-bottom", "1px solid var(--rp-hl-med)")
            property("padding", "2rem 1.5rem")
        }
    }) {
        Div({
            style {
                property("max-width", "1100px")
                property("margin", "0 auto")
                property("display", "grid")
                property("grid-template-columns", "repeat(auto-fit, minmax(240px, 1fr))")
                property("gap", "1.75rem")
                property("align-items", "center")
            }
        }) {
            val items = listOf(
                Triple("fa-solid fa-shield-halved", "Skatteverket-kompatibel", "Uppfyller lagkrav för elektronisk personalliggare"),
                Triple("fa-solid fa-file-invoice", "Färdigt löneunderlag", "Arbetade timmar, OB och raster sammanställt automatiskt"),
                Triple("fa-solid fa-temperature-arrow-down", "HACCP & Livsmedelsverk", "Digital temperatur- och hygienlogg utan papper"),
                Triple("fa-solid fa-mobile-screen-button", "100% mobilvänligt", "Inga dyra stämpelklockor eller kassa-terminaler behövs")
            )
            items.forEach { (icon, title, desc) ->
                Div({
                    style {
                        property("display", "flex")
                        property("align-items", "flex-start")
                        property("gap", "1rem")
                    }
                }) {
                    Div({
                        style {
                            property("width", "42px")
                            property("height", "42px")
                            property("border-radius", "10px")
                            property("background", "var(--rp-info-bg)")
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                            property("flex-shrink", "0")
                        }
                    }) {
                        FaIcon(icon, size = "1.1rem", color = "var(--rp-pine)")
                    }
                    Div {
                        Div({
                            style {
                                property("font-weight", "700")
                                property("font-size", "0.92rem")
                                property("color", "var(--rp-text)")
                                property("margin-bottom", "0.2rem")
                            }
                        }) { Text(title) }
                        Div({
                            style {
                                property("font-size", "0.82rem")
                                property("color", "var(--rp-subtle)")
                                property("line-height", "1.4")
                            }
                        }) { Text(desc) }
                    }
                }
            }
        }
    }
}

// ─────────────────────────────────────────
// INTERACTIVE ROI CALCULATOR
// ─────────────────────────────────────────
@Composable
fun LandingRoiCalculator(onSignUp: () -> Unit = {}) {
    var employeeCount by remember { mutableStateOf(8) }
    val savedHours = remember(employeeCount) { employeeCount * 2 }
    val savedMoney = remember(employeeCount) { employeeCount * 850 }

    Section({
        id("kalkylator")
        style {
            property("padding", "4.5rem 1.75rem")
            property("background", "var(--rp-surface)")
            property("border-top", "1px solid var(--rp-hl-med)")
            property("border-bottom", "1px solid var(--rp-hl-med)")
        }
    }) {
        Div({
            style {
                property("max-width", "900px")
                property("margin", "0 auto")
                property("background", "var(--rp-surface)")
                property("border-radius", "20px")
                property("padding", "2.5rem 2rem")
                property("box-shadow", "0 12px 32px rgba(15, 23, 42, 0.05)")
                property("border", "1px solid var(--rp-hl-med)")
            }
        }) {
            Div({
                style {
                    property("text-align", "center")
                    property("margin-bottom", "2rem")
                }
            }) {
                Span({
                    style {
                        property("background", "var(--rp-info-bg)")
                        property("color", "var(--rp-pine)")
                        property("font-size", "0.78rem")
                        property("font-weight", "800")
                        property("padding", "0.35rem 0.9rem")
                        property("border-radius", "6px")
                        property("letter-spacing", "0.06em")
                        property("text-transform", "uppercase")
                    }
                }) { Text("Tids- & Kostnadskalkylator") }

                H2({
                    style {
                        property("font-family", "'Plus Jakarta Sans', sans-serif")
                        property("font-size", "clamp(1.6rem, 3.2vw, 2.3rem)")
                        property("font-weight", "800")
                        property("color", "var(--rp-text)")
                        property("margin", "0.75rem 0 0.5rem")
                    }
                }) { Text("Hur mycket tid kan ditt café eller krog spara?") }

                P({
                    style { property("color", "var(--rp-subtle)"); property("font-size", "1rem") }
                }) { Text("Dra i reglaget för att beräkna din tidsbesparing varje månad.") }
            }

            // Slider container
            Div({
                style {
                    property("margin-bottom", "2.5rem")
                    property("padding", "1.5rem")
                    property("background", "var(--rp-overlay)")
                    property("border-radius", "14px")
                }
            }) {
                Div({
                    style {
                        property("display", "flex")
                        property("justify-content", "space-between")
                        property("align-items", "center")
                        property("margin-bottom", "1rem")
                    }
                }) {
                    Span({ style { property("font-weight", "700"); property("color", "var(--rp-text)") } }) { Text("Antal anställda:") }
                    Span({
                        style {
                            property("font-size", "1.4rem")
                            property("font-weight", "800")
                            property("color", "var(--rp-pine)")
                            property("background", "var(--rp-surface)")
                            property("padding", "0.2rem 1rem")
                            property("border-radius", "8px")
                            property("border", "1px solid var(--rp-hl-med)")
                        }
                    }) { Text("$employeeCount st") }
                }

                Input(type = org.jetbrains.compose.web.attributes.InputType.Range) {
                    value(employeeCount.toString())
                    attr("min", "2")
                    attr("max", "30")
                    onInput { ev ->
                        val v = ev.value
                        if (v != null) {
                            val parsed = v.toString().toIntOrNull()
                            if (parsed != null) employeeCount = parsed
                        }
                    }
                    style {
                        property("width", "100%")
                        property("height", "8px")
                        property("border-radius", "4px")
                        property("accent-color", "var(--rp-pine)")
                        property("cursor", "pointer")
                    }
                }
            }

            // Results grid
            Div({
                style {
                    property("display", "grid")
                    property("grid-template-columns", "repeat(auto-fit, minmax(220px, 1fr))")
                    property("gap", "1.5rem")
                    property("margin-bottom", "2rem")
                }
            }) {
                // Card 1
                Div({
                    style {
                        property("padding", "1.5rem")
                        property("background", "var(--rp-info-bg)")
                        property("border-radius", "14px")
                        property("border", "1px solid var(--rp-info-border)")
                        property("text-align", "center")
                    }
                }) {
                    Div({ style { property("font-size", "2.2rem"); property("font-weight", "800"); property("color", "var(--rp-pine)") } }) {
                        Text("~$savedHours tim/mån")
                    }
                    Div({ style { property("font-size", "0.88rem"); property("color", "var(--rp-pine)"); property("font-weight", "600"); property("margin-top", "0.25rem") } }) {
                        Text("Sparad administrationstid")
                    }
                }

                // Card 2
                Div({
                    style {
                        property("padding", "1.5rem")
                        property("background", "var(--rp-ok-bg)")
                        property("border-radius", "14px")
                        property("border", "1px solid var(--rp-ok-border)")
                        property("text-align", "center")
                    }
                }) {
                    Div({ style { property("font-size", "2.2rem"); property("font-weight", "800"); property("color", "var(--rp-ok-text)") } }) {
                        Text("~$savedMoney kr/mån")
                    }
                    Div({ style { property("font-size", "0.88rem"); property("color", "var(--rp-ok-text)"); property("font-weight", "600"); property("margin-top", "0.25rem") } }) {
                        Text("Minskade timspills-kostnader")
                    }
                }
            }

            Div({
                style { property("text-align", "center") }
            }) {
                Button({
                    onClick { onSignUp() }
                    style {
                        property("background", "var(--rp-pine)")
                        property("color", "white")
                        property("padding", "0.85rem 2rem")
                        property("border-radius", "8px")
                        property("font-size", "1rem")
                        property("font-weight", "700")
                        property("font-family", "inherit")
                        property("border", "none")
                        property("cursor", "pointer")
                        property("box-shadow", "0 4px 14px rgba(30,58,138,0.25)")
                    }
                }) {
                    Text("Börja spara tid — Testa 30 dagar gratis")
                }
            }
        }
    }
}

// ─────────────────────────────────────────
// FEATURES
// ─────────────────────────────────────────
@Composable
fun LandingFeatures() {
    Section({
        id("funktioner")
        style {
            property("padding", "5rem 1.75rem")
            property("background", "var(--rp-surface)")
            property("position", "relative")
            property("overflow", "hidden")
            property("border-top", "1px solid var(--rp-hl-med)")
        }
    }) {
        Div({
            style {
                property("max-width", "1100px")
                property("margin", "0 auto")
                property("position", "relative")
                property("z-index", "1")
            }
        }) {
            // Section heading
            Div({
                style {
                    property("text-align", "center")
                    property("margin-bottom", "3.5rem")
                }
            }) {
                Span({
                    style {
                        property("display", "inline-block")
                        property("background", "var(--rp-info-bg)")
                        property("color", "var(--rp-pine)")
                        property("font-size", "0.8rem")
                        property("font-weight", "800")
                        property("padding", "0.35rem 0.9rem")
                        property("border-radius", "6px")
                        property("letter-spacing", "0.06em")
                        property("text-transform", "uppercase")
                        property("margin-bottom", "0.75rem")
                    }
                }) { Text("Funktioner") }

                H2({
                    style {
                        property("font-family", "'Plus Jakarta Sans', system-ui, sans-serif")
                        property("font-size", "clamp(1.75rem, 4vw, 2.75rem)")
                        property("font-weight", "800")
                        property("color", "var(--rp-text)")
                        property("margin", "0")
                        property("letter-spacing", "-0.02em")
                    }
                }) { Text(LocaleManager.dictionary.allSmallPlaceNeeds) }
            }

            // Feature grid
            Div({
                style {
                    property("display", "grid")
                    property("grid-template-columns", "repeat(auto-fit, minmax(240px, 1fr))")
                    property("gap", "1.5rem")
                }
            }) {
                val dict = LocaleManager.dictionary
                featureCard(
                    icon = "fa-solid fa-clock",
                    number = "01",
                    title = dict.feature1Title,
                    description = dict.feature1Desc
                )
                featureCard(
                    icon = "fa-solid fa-clipboard-list",
                    number = "02",
                    title = dict.feature2Title,
                    description = dict.feature2Desc
                )
                featureCard(
                    icon = "fa-solid fa-money-bill-wave",
                    number = "03",
                    title = dict.feature3Title,
                    description = dict.feature3Desc
                )
                featureCard(
                    icon = "fa-solid fa-bell",
                    number = "04",
                    title = dict.feature4Title,
                    description = dict.feature4Desc
                )
            }
        }
    }
}

@Composable
private fun featureCard(icon: String, number: String, title: String, description: String) {
    Div({
        classes("feature-card")
        style {
            property("background", "var(--rp-overlay)")
            property("border", "1px solid var(--rp-hl-med)")
            property("border-radius", "16px")
            property("padding", "1.75rem")
            property("box-shadow", "0 2px 8px rgba(15, 23, 42, 0.04)")
        }
    }) {
        // Number badge + icon row
        Div({
            style {
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "space-between")
                property("margin-bottom", "1.25rem")
            }
        }) {
            Span({
                style {
                    property("font-family", "'Plus Jakarta Sans', system-ui, sans-serif")
                    property("font-size", "0.75rem")
                    property("font-weight", "800")
                    property("color", "var(--rp-pine)")
                    property("letter-spacing", "0.08em")
                    property("background", "var(--rp-info-bg)")
                    property("padding", "0.2rem 0.6rem")
                    property("border-radius", "6px")
                    property("border", "1px solid var(--rp-info-border)")
                }
            }) { Text(number) }
            Div({
                style {
                    property("width", "42px")
                    property("height", "42px")
                    property("border-radius", "10px")
                    property("background", "var(--rp-info-bg)")
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                }
            }) {
                FaIcon(icon, size = "1.1rem", color = "var(--rp-pine)")
            }
        }
        H3({
            style {
                property("font-size", "1.1rem")
                property("font-weight", "700")
                property("color", "var(--rp-text)")
                property("margin", "0 0 0.6rem 0")
                property("letter-spacing", "-0.01em")
            }
        }) { Text(title) }
        P({
            style {
                property("font-size", "0.9rem")
                property("color", "var(--rp-subtle)")
                property("line-height", "1.65")
                property("margin", "0")
            }
        }) { Text(description) }
    }
}

// ─────────────────────────────────────────
// WHY MITTPASS
// ─────────────────────────────────────────
@Composable
fun LandingWhy() {
    Section({
        style {
            property("padding", "5rem 1.75rem")
            property("background", "var(--rp-surface)")
            property("border-top", "1px solid var(--rp-hl-med)")
        }
    }) {
        Div({
            style {
                property("max-width", "1100px")
                property("margin", "0 auto")
            }
        }) {
            Div({
                style {
                    property("display", "grid")
                    property("grid-template-columns", "repeat(auto-fit, minmax(280px, 1fr))")
                    property("gap", "3rem 4rem")
                    property("align-items", "center")
                }
            }) {
                // Left: headline + tags
                Div {
                    Span({
                        style {
                            property("display", "inline-block")
                            property("background", "var(--rp-info-bg)")
                            property("color", "var(--rp-pine)")
                            property("font-size", "0.8rem")
                            property("font-weight", "800")
                            property("padding", "0.35rem 0.9rem")
                            property("border-radius", "6px")
                            property("letter-spacing", "0.06em")
                            property("text-transform", "uppercase")
                            property("margin-bottom", "0.75rem")
                        }
                    }) { Text("Bakgrund & Filosofi") }

                    H2({
                        style {
                            property("font-family", "'Plus Jakarta Sans', system-ui, sans-serif")
                            property("font-size", "clamp(1.75rem, 3.5vw, 2.5rem)")
                            property("font-weight", "800")
                            property("color", "var(--rp-text)")
                            property("margin", "0 0 1.25rem 0")
                            property("letter-spacing", "-0.02em")
                            property("line-height", "1.2")
                        }
                    }) { Text(LocaleManager.dictionary.builtByOwner) }
                    P({
                        style {
                            property("font-size", "1.05rem")
                            property("color", "var(--rp-subtle)")
                            property("line-height", "1.8")
                            property("margin", "0 0 1.75rem 0")
                            property("direction", "ltr")
                            property("text-align", "left")
                        }
                    }) {
                        Text(LocaleManager.dictionary.ownerQuote)
                    }
                    Div({
                        style {
                            property("display", "flex")
                            property("flex-wrap", "wrap")
                            property("gap", "0.6rem")
                        }
                    }) {
                        val dict = LocaleManager.dictionary
                        uspTag(dict.uspCafePizzeria)
                        uspTag(dict.uspStaffRegister)
                        uspTag(dict.uspNoHardware)
                        uspTag(dict.uspPushNotifications)
                    }
                }

                // Right: stat cards
                Div({
                    style {
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("gap", "1rem")
                    }
                }) {
                    val dict = LocaleManager.dictionary
                    statCard(dict.stat1Title, dict.stat1Desc)
                    statCard(dict.stat2Title, dict.stat2Desc)
                    statCard(dict.stat3Title, dict.stat3Desc)
                }
            }
        }
    }
}

@Composable
private fun uspTag(text: String) {
    Span({
        style {
            property("background", "var(--rp-info-bg)")
            property("color", "var(--rp-pine)")
            property("padding", "0.4rem 0.9rem")
            property("border-radius", "6px")
            property("border", "1px solid var(--rp-info-border)")
            property("font-size", "0.85rem")
            property("font-weight", "600")
        }
    }) { Text(text) }
}

@Composable
private fun statCard(stat: String, description: String) {
    Div({
        style {
            property("background", "var(--rp-overlay)")
            property("border", "1px solid var(--rp-hl-med)")
            property("border-radius", "14px")
            property("padding", "1.25rem 1.5rem")
            property("display", "flex")
            property("align-items", "center")
            property("gap", "1.25rem")
            property("box-shadow", "0 2px 6px rgba(15, 23, 42, 0.03)")
        }
    }) {
        Div({
            style {
                property("font-family", "'Plus Jakarta Sans', system-ui, sans-serif")
                property("font-size", "1.75rem")
                property("font-weight", "800")
                property("color", "var(--rp-pine)")
                property("flex-shrink", "0")
                property("min-width", "64px")
            }
        }) { Text(stat) }
        Div({
            style {
                property("font-size", "0.9rem")
                property("color", "var(--rp-subtle)")
                property("line-height", "1.5")
            }
        }) { Text(description) }
    }
}

// ─────────────────────────────────────────
// PRICING
// ─────────────────────────────────────────
@Composable
fun LandingPricing(onSignUp: () -> Unit = {}) {
    Section({
        id("priser")
        style {
            property("padding", "5rem 1.75rem")
            property("background", "var(--rp-overlay)")
            property("border-top", "1px solid var(--rp-hl-med)")
        }
    }) {
        Div({
            style {
                property("max-width", "640px")
                property("margin", "0 auto")
                property("text-align", "center")
            }
        }) {
            Span({
                style {
                    property("display", "inline-block")
                    property("background", "var(--rp-info-bg)")
                    property("color", "var(--rp-pine)")
                    property("font-size", "0.8rem")
                    property("font-weight", "800")
                    property("padding", "0.35rem 0.9rem")
                    property("border-radius", "6px")
                    property("letter-spacing", "0.06em")
                    property("text-transform", "uppercase")
                    property("margin-bottom", "0.75rem")
                }
            }) { Text("Prissättning") }

            H2({
                style {
                    property("font-family", "'Plus Jakarta Sans', system-ui, sans-serif")
                    property("font-size", "clamp(1.75rem, 4vw, 2.75rem)")
                    property("font-weight", "800")
                    property("color", "var(--rp-text)")
                    property("margin", "0 0 0.75rem 0")
                    property("letter-spacing", "-0.02em")
                }
            }) { Text(LocaleManager.dictionary.simpleTransparent) }
            P({
                style {
                    property("font-size", "1.05rem")
                    property("color", "var(--rp-subtle)")
                    property("margin", "0 0 2.5rem 0")
                }
            }) { Text(LocaleManager.dictionary.trialInfo) }

            // Pricing card
            Div({
                classes("pricing-card")
                style {
                    property("background", "var(--rp-surface)")
                    property("border", "1px solid var(--rp-hl-med)")
                    property("border-radius", "20px")
                    property("padding", "2.5rem 2.25rem")
                    property("box-shadow", "0 10px 30px rgba(15, 23, 42, 0.08)")
                    property("text-align", "left")
                }
            }) {
                // Price header
                Div({
                    style {
                        property("display", "flex")
                        property("align-items", "baseline")
                        property("gap", "0.5rem")
                        property("margin-bottom", "0.4rem")
                    }
                }) {
                    Span({
                        style {
                            property("font-family", "'Plus Jakarta Sans', system-ui, sans-serif")
                            property("font-size", "3.5rem")
                            property("font-weight", "800")
                            property("color", "var(--rp-text)")
                            property("line-height", "1")
                        }
                    }) { Text(LocaleManager.dictionary.krPerUser) }
                    Span({
                        style {
                            property("font-size", "0.95rem")
                            property("color", "var(--rp-subtle)")
                            property("font-weight", "600")
                        }
                    }) { Text(LocaleManager.dictionary.perUserMonth) }
                }
                Span({
                    style {
                        property("font-size", "0.85rem")
                        property("color", "var(--rp-subtle)")
                        property("display", "block")
                        property("margin-bottom", "1.75rem")
                    }
                }) { Text(LocaleManager.dictionary.exVatInfo) }

                // Divider
                Div({
                    style {
                        property("height", "1px")
                        property("background", "var(--rp-overlay)")
                        property("margin-bottom", "1.75rem")
                    }
                })

                val dict = LocaleManager.dictionary
                // Feature list
                Div({
                    style {
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("gap", "0.85rem")
                        property("margin-bottom", "2.25rem")
                    }
                }) {
                    listOf(
                        dict.priceFeature1,
                        dict.priceFeature2,
                        dict.priceFeature3,
                        dict.priceFeature4,
                        dict.priceFeature5,
                        dict.priceFeature6,
                        dict.priceFeature7
                    ).forEach { item ->
                        Div({
                            style {
                                property("display", "flex")
                                property("align-items", "flex-start")
                                property("gap", "0.75rem")
                            }
                        }) {
                            Span({
                                style {
                                    property("flex-shrink", "0")
                                    property("margin-top", "0.15rem")
                                    property("width", "18px")
                                    property("height", "18px")
                                    property("border-radius", "50%")
                                    property("background", "var(--rp-info-bg)")
                                    property("display", "flex")
                                    property("align-items", "center")
                                    property("justify-content", "center")
                                }
                            }) { FaIcon("fa-solid fa-check", size = "0.75rem", color = "var(--rp-pine)") }
                            Span({
                                style {
                                    property("font-size", "0.95rem")
                                    property("color", "var(--rp-text)")
                                    property("line-height", "1.5")
                                }
                            }) { Text(item) }
                        }
                    }
                }

                // CTA
                Button(attrs = {
                    onClick { onSignUp() }
                    style {
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("gap", "0.5rem")
                        property("width", "100%")
                        property("padding", "1rem 2rem")
                        property("font-size", "1.05rem")
                        property("font-weight", "700")
                        property("font-family", "inherit")
                        property("background", "var(--rp-pine)")
                        property("color", "white")
                        property("border", "none")
                        property("border-radius", "8px")
                        property("cursor", "pointer")
                        property("min-height", "52px")
                        property("box-shadow", "0 4px 14px rgba(30,58,138,0.28)")
                        property("letter-spacing", "0.01em")
                        property("transition", "transform 0.15s ease, box-shadow 0.15s ease")
                    }
                }) {
                    Text(LocaleManager.dictionary.startFree30)
                    FaIcon("fa-solid fa-arrow-right", size = "0.85rem")
                }

                Div({
                    style {
                        property("display", "flex")
                        property("justify-content", "center")
                        property("gap", "1rem")
                        property("margin-top", "1.25rem")
                        property("font-size", "0.85rem")
                        property("color", "var(--rp-subtle)")
                        property("flex-wrap", "wrap")
                    }
                }) {
                    Span { Text("Inga dolda avgifter") }
                    Span { Text("•") }
                    Span { Text("Ingen bindningstid") }
                    Span { Text("•") }
                    Span { Text("Avsluta när du vill") }
                }
            }
        }
    }
}

// ─────────────────────────────────────────
// FAQ
// ─────────────────────────────────────────
@Composable
fun LandingFaq() {
    Section({
        id("faq")
        style {
            property("padding", "5rem 1.75rem")
            property("background", "var(--rp-surface)")
            property("border-top", "1px solid var(--rp-hl-med)")
        }
    }) {
        Div({
            style {
                property("max-width", "750px")
                property("margin", "0 auto")
            }
        }) {
            Div({
                style { property("text-align", "center"); property("margin-bottom", "3rem") }
            }) {
                Span({
                    style {
                        property("display", "inline-block")
                        property("background", "var(--rp-info-bg)")
                        property("color", "var(--rp-pine)")
                        property("font-size", "0.8rem")
                        property("font-weight", "800")
                        property("padding", "0.35rem 0.9rem")
                        property("border-radius", "6px")
                        property("letter-spacing", "0.06em")
                        property("text-transform", "uppercase")
                        property("margin-bottom", "0.75rem")
                    }
                }) { Text("Frågor & Svar") }

                H2({
                    style {
                        property("font-family", "'Plus Jakarta Sans', system-ui, sans-serif")
                        property("font-size", "clamp(1.75rem, 4vw, 2.5rem)")
                        property("font-weight", "800")
                        property("color", "var(--rp-text)")
                        property("margin", "0")
                        property("letter-spacing", "-0.02em")
                    }
                }) { Text(LocaleManager.dictionary.commonQuestions) }
            }

            val dict = LocaleManager.dictionary
            faqItem(
                dict.faq1Question,
                dict.faq1Answer
            )
            faqItem(
                dict.faq2Question,
                dict.faq2Answer
            )
            faqItem(
                dict.faq3Question,
                dict.faq3Answer
            )
            faqItem(
                dict.faq4Question,
                dict.faq4Answer
            )
            faqItem(
                dict.faq5Question,
                dict.faq5Answer
            )
        }
    }
}

@Composable
private fun faqItem(question: String, answer: String) {
    Div({
        style {
            property("padding", "1.5rem 0")
            property("border-bottom", "1px solid var(--rp-hl-med)")
        }
    }) {
        Div({
            style {
                property("display", "flex")
                property("align-items", "flex-start")
                property("gap", "0.875rem")
                property("margin-bottom", "0.5rem")
            }
        }) {
            Span({
                style {
                    property("flex-shrink", "0")
                    property("margin-top", "0.15rem")
                }
            }) { FaIcon("fa-solid fa-circle-question", size = "0.95rem", color = "var(--rp-pine)") }
            H3({
                style {
                    property("font-size", "1.05rem")
                    property("font-weight", "700")
                    property("color", "var(--rp-text)")
                    property("margin", "0")
                    property("line-height", "1.4")
                }
            }) { Text(question) }
        }
        P({
            style {
                property("font-size", "0.95rem")
                property("color", "var(--rp-subtle)")
                property("line-height", "1.7")
                property("margin", "0 0 0 1.85rem")
            }
        }) { Text(answer) }
    }
}

// ─────────────────────────────────────────
// CONTACT
// ─────────────────────────────────────────
private val contactScope = MainScope()
private val feedbackRepo = FeedbackRepository(httpClient)

@Composable
fun LandingContact() {
    var name by remember { mutableStateOf("") }
    var email by remember { mutableStateOf("") }
    var message by remember { mutableStateOf("") }
    var sending by remember { mutableStateOf(false) }
    var sent by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    Section({
        id("kontakta-oss")
        style {
            property("padding", "5rem 1.75rem")
            property("background", "var(--rp-surface)")
        }
    }) {
        Div({
            style {
                property("max-width", "600px")
                property("margin", "0 auto")
            }
        }) {
            Div({
                style { property("text-align", "center") }
            }) {
                H2({
                    style {
                        property("font-family", "var(--font-serif)")
                        property("font-size", "clamp(1.75rem, 4vw, 2.5rem)")
                        property("font-weight", "800")
                        property("color", "var(--rp-text)")
                        property("margin", "0 0 0.75rem 0")
                        property("letter-spacing", "-0.02em")
                    }
                }) { Text(LocaleManager.dictionary.contactUs) }
                P({
                    style {
                        property("color", "var(--rp-subtle)")
                        property("font-size", "1rem")
                        property("margin", "0 0 2.5rem 0")
                        property("line-height", "1.6")
                    }
                }) { Text(LocaleManager.dictionary.contactSub) }
            }

            if (sent) {
                Div({
                    style {
                        property("background", "var(--rp-ok-bg)")
                        property("border", "1px solid var(--rp-ok-border)")
                        property("border-radius", "12px")
                        property("padding", "1.5rem")
                        property("text-align", "center")
                        property("color", "var(--rp-ok-text)")
                        property("font-weight", "600")
                    }
                }) { Text(LocaleManager.dictionary.contactSuccess) }
            } else {
                Div({
                    style {
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("gap", "1rem")
                    }
                }) {
                    val dict = LocaleManager.dictionary
                    // Name
                    Input(InputType.Text) {
                        value(name)
                        placeholder(dict.contactNamePlaceholder)
                        onInput { name = it.value }
                        style {
                            property("width", "100%")
                            property("padding", "0.75rem 1rem")
                            property("border", "1px solid var(--rp-hl-med)")
                            property("border-radius", "8px")
                            property("font-size", "0.95rem")
                            property("font-family", "inherit")
                            property("outline", "none")
                            property("box-sizing", "border-box")
                        }
                    }
                    // Email
                    Input(InputType.Email) {
                        value(email)
                        placeholder(dict.contactEmailPlaceholder)
                        onInput { email = it.value }
                        style {
                            property("width", "100%")
                            property("padding", "0.75rem 1rem")
                            property("border", "1px solid var(--rp-hl-med)")
                            property("border-radius", "8px")
                            property("font-size", "0.95rem")
                            property("font-family", "inherit")
                            property("outline", "none")
                            property("box-sizing", "border-box")
                        }
                    }
                    // Message
                    TextArea {
                        value(message)
                        onInput { message = it.value }
                        attr("rows", "5")
                        attr("placeholder", dict.contactMessagePlaceholder)
                        style {
                            property("width", "100%")
                            property("padding", "0.75rem 1rem")
                            property("border", "1px solid var(--rp-hl-med)")
                            property("border-radius", "8px")
                            property("font-size", "0.95rem")
                            property("font-family", "inherit")
                            property("resize", "vertical")
                            property("outline", "none")
                            property("box-sizing", "border-box")
                        }
                    }

                    if (error != null) {
                        Div({
                            style {
                                property("color", "var(--rp-err-text)")
                                property("font-size", "0.875rem")
                            }
                        }) { Text(error!!) }
                    }

                    Button({
                        onClick {
                            if (name.isBlank() || email.isBlank() || message.isBlank()) {
                                error = dict.contactFillAllFields
                                return@onClick
                            }
                            error = null
                            sending = true
                            contactScope.launch {
                                feedbackRepo.send(FeedbackRequest(name = name, email = email, message = message))
                                    .onSuccess { sent = true }
                                    .onFailure { error = dict.contactGenericError }
                                sending = false
                            }
                        }
                        if (sending) disabled()
                        style {
                            property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))")
                            property("color", "#FFFFFF")
                            property("border", "none")
                            property("border-radius", "8px")
                            property("padding", "0.85rem 2rem")
                            property("font-size", "1rem")
                            property("font-weight", "600")
                            property("cursor", if (sending) "not-allowed" else "pointer")
                            property("opacity", if (sending) "0.7" else "1")
                            property("transition", "opacity 0.2s")
                            property("align-self", "flex-start")
                        }
                    }) { Text(if (sending) dict.contactSending else dict.contactSendBtn) }
                }
            }
        }
    }
}

// ─────────────────────────────────────────
// FOOTER
// ─────────────────────────────────────────
@Composable
fun LandingFooter(
    onPrivacy: () -> Unit = {},
    onTerms: () -> Unit = {},
    onDpa: () -> Unit = {},
    onDeleteAccount: () -> Unit = {},
    onSupport: () -> Unit = {},
    onNavigate: (String) -> Unit = {}
) {
    Footer({
        id("kontakt")
        style {
            property("padding", "3.5rem 1.75rem")
            property("background", "var(--rp-pine)")
        }
    }) {
        Div({
            style {
                property("max-width", "1100px")
                property("margin", "0 auto")
            }
        }) {
            // Top row: logo + nav
            Div({
                style {
                    property("display", "flex")
                    property("align-items", "flex-start")
                    property("justify-content", "space-between")
                    property("flex-wrap", "wrap")
                    property("gap", "2rem")
                    property("margin-bottom", "2.5rem")
                    property("padding-bottom", "2rem")
                    property("border-bottom", "1px solid rgba(255,255,255,0.08)")
                }
            }) {
                // Brand + tagline
                Div {
                    // Logo + wordmark in footer
                    Div({
                        style {
                            property("display", "flex"); property("align-items", "center")
                            property("gap", "0.5rem"); property("margin-bottom", "0.75rem")
                        }
                    }) {
                        Img(src = "/mittpass_logo.svg", alt = "Mittpass – Schemaläggning, Personalliggare & HACCP") {
                            style {
                                property("width", "32px"); property("height", "32px")
                                property("border-radius", "6px")
                            }
                        }
                        Span({
                            style {
                                property("font-size", "1rem"); property("font-weight", "800")
                                property("color", "rgba(255,255,255,0.9)"); property("letter-spacing", "-0.2px")
                            }
                        }) { Text("Mittpass") }
                    }
                    val dict = LocaleManager.dictionary
                    P({
                        style {
                            property("font-size", "0.85rem")
                            property("color", "rgba(255,255,255,0.45)")
                            property("margin", "0")
                            property("max-width", "280px")
                            property("line-height", "1.6")
                        }
                    }) { Text(dict.footerTagline) }
                }

                // Nav columns
                Div({
                    style {
                        property("display", "flex")
                        property("gap", "3rem")
                        property("flex-wrap", "wrap")
                    }
                }) {
                    val dict = LocaleManager.dictionary
                    footerNavCol(
                        "Lösningar & Moduler",
                        listOf(
                            Triple("Elektronisk personalliggare", "/personalliggare", "personalliggare"),
                            Triple("Schemaläggning personal", "/schemalagga-personal", "schemalagga"),
                            Triple("Digital egenkontroll", "/egenkontroll", "egenkontroll"),
                            Triple("Livsmedelskontroll", "/livsmedelskontroll", "livsmedelskontroll"),
                            Triple("Digitalt HACCP-system", "/haccp", "haccp")
                        ),
                        onNavigate = onNavigate
                    )
                    footerCol(
                        dict.footerProduct,
                        listOf(dict.footerFunktioner to "/#funktioner", dict.footerPriser to "/#priser", dict.footerFaq to "/#faq")
                    )
                    footerCol(
                        dict.footerKontakt,
                        listOf(
                            dict.footerEmail to "mailto:kontakt@mittpass.se",
                            "Google Play App" to "https://play.google.com/store/apps/details?id=se.mittpass.app&hl=en",
                            "AI & LLM-dokumentation" to "/llms.txt"
                        )
                    )
                }
            }

            // Bottom row
            Div({
                style {
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "space-between")
                    property("flex-wrap", "wrap")
                    property("gap", "1rem")
                }
            }) {
                val dict = LocaleManager.dictionary
                P({
                    style {
                        property("font-size", "0.8rem")
                        property("color", "rgba(255,255,255,0.35)")
                        property("margin", "0")
                    }
                }) { Text(dict.footerAllRights) }

                Div({
                    style {
                        property("display", "flex")
                        property("gap", "1.25rem")
                        property("flex-wrap", "wrap")
                    }
                }) {
                    footerBottomLink(dict.footerPrivacy, "/privacy") { onPrivacy() }
                    footerBottomLink(dict.footerTerms, "/terms") { onTerms() }
                    footerBottomLink(dict.footerDpa, "/dpa") { onDpa() }
                    footerBottomLink(dict.footerDeleteAccount, "/delete-account") { onDeleteAccount() }
                    footerBottomLink(dict.footerSupport, "/support") { onSupport() }
                }
            }
        }
    }
}

@Composable
private fun footerBottomLink(label: String, path: String, onClick: () -> Unit) {
    A(attrs = {
        href(path)
        onClick { it.preventDefault(); onClick() }
        style {
            property("color", "rgba(255,255,255,0.35)")
            property("font-size", "0.8rem")
            property("cursor", "pointer")
            property("text-decoration", "none")
            property("font-family", "inherit")
        }
    }) { Text(label) }
}

@Composable
private fun footerNavCol(heading: String, links: List<Triple<String, String, String>>, onNavigate: (String) -> Unit) {
    Div {
        Span({
            style {
                property("display", "block")
                property("font-size", "0.75rem")
                property("font-weight", "700")
                property("color", "rgba(255,255,255,0.5)")
                property("text-transform", "uppercase")
                property("letter-spacing", "0.08em")
                property("margin-bottom", "0.875rem")
            }
        }) { Text(heading) }
        Div({
            style {
                property("display", "flex")
                property("flex-direction", "column")
                property("gap", "0.6rem")
            }
        }) {
            links.forEach { (label, url, navKey) ->
                A(attrs = {
                    href(url)
                    onClick { it.preventDefault(); onNavigate(navKey) }
                    style {
                        property("color", "rgba(255,255,255,0.65)")
                        property("text-decoration", "none")
                        property("font-size", "0.875rem")
                        property("font-weight", "500")
                        property("cursor", "pointer")
                    }
                }) { Text(label) }
            }
        }
    }
}

@Composable
private fun footerCol(heading: String, links: List<Pair<String, String>>) {
    Div {
        Span({
            style {
                property("display", "block")
                property("font-size", "0.75rem")
                property("font-weight", "700")
                property("color", "rgba(255,255,255,0.5)")
                property("text-transform", "uppercase")
                property("letter-spacing", "0.08em")
                property("margin-bottom", "0.875rem")
            }
        }) { Text(heading) }
        Div({
            style {
                property("display", "flex")
                property("flex-direction", "column")
                property("gap", "0.6rem")
            }
        }) {
            links.forEach { (label, url) ->
                A(attrs = {
                    href(url)
                    style {
                        property("color", "rgba(255,255,255,0.65)")
                        property("text-decoration", "none")
                        property("font-size", "0.875rem")
                        property("font-weight", "500")
                    }
                }) { Text(label) }
            }
        }
    }
}


// ─────────────────────────────────────────
// HOW IT WORKS - TIMELINE
// ─────────────────────────────────────────
@Composable
fun LandingHowItWorks() {
    var activeTab by remember { mutableStateOf("personal") }
    val dict = LocaleManager.dictionary

    Section({
        style {
            property("padding", "5.5rem 1.75rem")
            property("background", "var(--rp-surface)")
            property("position", "relative")
            property("overflow", "hidden")
        }
    }) {
        // Faint background blob
        Div({ style {
            property("position", "absolute"); property("bottom", "-100px"); property("left", "50%")
            property("transform", "translateX(-50%)")
            property("width", "700px"); property("height", "400px")
            property("background", "radial-gradient(ellipse, rgba(44,63,168,0.05) 0%, transparent 70%)")
            property("pointer-events", "none")
        } })

        Div({ style {
            property("max-width", "1100px"); property("margin", "0 auto")
            property("position", "relative"); property("z-index", "1")
        } }) {
            Div({ style { property("text-align", "center"); property("margin-bottom", "3rem") } }) {
                Span({
                    style {
                        property("display", "inline-block")
                        property("background", "var(--rp-info-bg)")
                        property("color", "var(--rp-pine)")
                        property("font-size", "0.8rem")
                        property("font-weight", "800")
                        property("padding", "0.35rem 0.9rem")
                        property("border-radius", "6px")
                        property("letter-spacing", "0.06em")
                        property("text-transform", "uppercase")
                        property("margin-bottom", "0.75rem")
                    }
                }) { Text("Arbetsflöde") }

                H2({ style {
                    property("font-family", "'Plus Jakarta Sans', system-ui, sans-serif")
                    property("font-size", "clamp(1.75rem, 4vw, 2.75rem)")
                    property("font-weight", "800"); property("color", "var(--rp-text)")
                    property("margin", "0"); property("letter-spacing", "-0.02em")
                } }) { Text(dict.howItWorksTitle) }
                P({ style {
                    property("margin", "0.75rem auto 0"); property("max-width", "560px")
                    property("color", "var(--rp-subtle)"); property("font-size", "1.05rem"); property("line-height", "1.7")
                } }) { Text(dict.howItWorksSub) }
            }

            // Tab Toggle Switch
            Div({ classes("timeline-toggle-container") }) {
                Button({
                    classes("timeline-toggle-btn")
                    if (activeTab == "personal") classes("active")
                    onClick { activeTab = "personal" }
                }) {
                    FaIcon("fa-solid fa-users-gear", size = "1rem", color = if (activeTab == "personal") "var(--rp-surface)" else "var(--rp-subtle)")
                    Span({ style { property("margin-left", "0.5rem") } }) { Text(dict.timelineTabStaff) }
                }
                Button({
                    classes("timeline-toggle-btn")
                    if (activeTab == "haccp") classes("active")
                    onClick { activeTab = "haccp" }
                }) {
                    FaIcon("fa-solid fa-temperature-half", size = "1rem", color = if (activeTab == "haccp") "var(--rp-surface)" else "var(--rp-subtle)")
                    Span({ style { property("margin-left", "0.5rem") } }) { Text(dict.timelineTabHaccp) }
                }
            }

            Div({ classes("timeline-grid") }) {
                Div({ classes("timeline-line") })

                if (activeTab == "personal") {
                    timelineStep("01", "fa-solid fa-calendar-plus", "var(--rp-info-bg)", "var(--rp-pine)",
                        dict.timelineStaffStep1Title, dict.timelineStaffStep1Desc)
                    timelineStep("02", "fa-solid fa-mobile-screen-button", "var(--rp-ok-bg)", "var(--rp-ok-text)",
                        dict.timelineStaffStep2Title, dict.timelineStaffStep2Desc)
                    timelineStep("03", "fa-solid fa-clock", "var(--rp-warn-bg)", "var(--rp-warn-text)",
                        dict.timelineStaffStep3Title, dict.timelineStaffStep3Desc)
                    timelineStep("04", "fa-solid fa-file-invoice-dollar", "var(--rp-info-bg)", "var(--rp-pine)",
                        dict.timelineStaffStep4Title, dict.timelineStaffStep4Desc)
                } else {
                    timelineStep("01", "fa-solid fa-list-check", "var(--rp-info-bg)", "var(--rp-pine)",
                        dict.timelineHaccpStep1Title, dict.timelineHaccpStep1Desc)
                    timelineStep("02", "fa-solid fa-mobile-screen-button", "var(--rp-ok-bg)", "var(--rp-ok-text)",
                        dict.timelineHaccpStep2Title, dict.timelineHaccpStep2Desc)
                    timelineStep("03", "fa-solid fa-bell", "var(--rp-warn-bg)", "var(--rp-warn-text)",
                        dict.timelineHaccpStep3Title, dict.timelineHaccpStep3Desc)
                    timelineStep("04", "fa-solid fa-file-shield", "var(--rp-info-bg)", "var(--rp-pine)",
                        dict.timelineHaccpStep4Title, dict.timelineHaccpStep4Desc)
                }
            }
        }
    }
}

@Composable
private fun timelineStep(num: String, icon: String, bgLight: String, accent: String, title: String, body: String) {
    Div({ classes("timeline-step", "reveal") }) {
        Div({ classes("timeline-badge"); style {
            property("background", bgLight)
            property("color", accent)
            property("position", "relative")
        } }) {
            FaIcon(icon, size = "1.6rem", color = accent)
            Span({ style {
                property("position", "absolute"); property("top", "-4px"); property("right", "-4px")
                property("width", "20px"); property("height", "20px"); property("border-radius", "50%")
                property("background", accent); property("color", "white")
                property("font-size", "0.6rem"); property("font-weight", "800")
                property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                property("font-family", "var(--font-serif)")
            } }) { Text(num) }
        }
        Div({ classes("timeline-content") }) {
            H3({ style {
                property("font-family", "'Manrope', sans-serif")
                property("margin", "0 0 0.5rem 0")
            } }) { Text(title) }
            P({ style {
                property("margin", "0")
            } }) { Text(body) }
        }
    }
}

// ─────────────────────────────────────────
// SCREENSHOT SHOWCASE
// ─────────────────────────────────────────
@Composable
fun LandingPhotoSection() {
    var activeTab by remember { mutableStateOf(0) }
    var activeSchemaSubTab by remember { mutableStateOf(0) }
    val dict = LocaleManager.dictionary

    val tabs: List<Triple<String, String, String>> = listOf(
        Triple("fa-solid fa-calendar-days", dict.showcaseTabSchema, "screen_schema_week.png"),
        Triple("fa-solid fa-users", dict.showcaseTabPersonal, "screen_personal.png"),
        Triple("fa-solid fa-clipboard-list", dict.showcaseTabLiggare, "screen_personalliggare.png"),
        Triple("fa-solid fa-money-bill-wave", dict.showcaseTabLoner, "screen_loner.png"),
        Triple("fa-solid fa-calendar-xmark", dict.showcaseTabFranvaro, "screen_franvaro.png"),
        Triple("fa-solid fa-flask", dict.showcaseTabEgenkontroll, "screen_egenkontroll.png"),
        Triple("fa-solid fa-file-lines", dict.showcaseTabInspektion, "screen_inspektionsrapport.png"),
    )

    Section({ style {
        property("padding", "5.5rem 1.75rem")
        property("background", "var(--rp-overlay)")
        property("position", "relative")
        property("overflow", "hidden")
        property("border-top", "1px solid var(--rp-hl-med)")
    } }) {
        Div({ style {
            property("max-width", "1100px"); property("margin", "0 auto")
            property("position", "relative"); property("z-index", "1")
        } }) {
            // Heading
            Div({ style { property("text-align", "center"); property("margin-bottom", "2.5rem") } }) {
                Span({
                    style {
                        property("display", "inline-block")
                        property("background", "var(--rp-info-bg)")
                        property("color", "var(--rp-pine)")
                        property("font-size", "0.8rem")
                        property("font-weight", "800")
                        property("padding", "0.35rem 0.9rem")
                        property("border-radius", "6px")
                        property("letter-spacing", "0.06em")
                        property("text-transform", "uppercase")
                        property("margin-bottom", "0.75rem")
                    }
                }) { Text("Systemöversikt") }

                H2({ style {
                    property("font-family", "'Plus Jakarta Sans', system-ui, sans-serif")
                    property("font-size", "clamp(1.75rem, 4vw, 2.75rem)")
                    property("font-weight", "800"); property("color", "var(--rp-text)")
                    property("margin", "0 0 0.75rem 0"); property("letter-spacing", "-0.02em")
                } }) { Text(dict.showcaseTitle) }
                P({ style {
                    property("margin", "0 auto"); property("max-width", "480px")
                    property("color", "var(--rp-subtle)"); property("font-size", "1rem")
                } }) { Text(dict.showcaseSub) }
            }

            // Tab bar
            Div({ style {
                property("display", "flex"); property("gap", "0.5rem"); property("flex-wrap", "wrap")
                property("justify-content", "center"); property("margin-bottom", "1.75rem")
            } }) {
                tabs.forEachIndexed { idx, (icon, label, _) ->
                    Button({
                        onClick { activeTab = idx }
                        style {
                            property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.45rem")
                            property("padding", "0.55rem 1.1rem"); property("border-radius", "8px")
                            property("font-size", "0.85rem"); property("font-weight", "700"); property("font-family", "inherit")
                            property("cursor", "pointer"); property("transition", "all 0.15s ease")
                            property("border", if (idx == activeTab) "1px solid var(--rp-pine)" else "1px solid var(--rp-hl-med)")
                            property("background", if (idx == activeTab) "var(--rp-pine)" else "var(--rp-surface)")
                            property("color", if (idx == activeTab) "white" else "var(--rp-subtle)")
                            property("box-shadow", if (idx == activeTab) "0 2px 6px rgba(30,58,138,0.2)" else "0 1px 2px rgba(0,0,0,0.03)")
                            property("white-space", "nowrap")
                        }
                    }) {
                        FaIcon(icon, size = "0.8rem", color = if (idx == activeTab) "white" else "var(--rp-subtle)")
                        Text(" $label")
                    }
                }
            }

            // Sub-tabs for Schema
            if (activeTab == 0) {
                Div({ style {
                    property("display", "flex"); property("gap", "0.4rem")
                    property("justify-content", "center"); property("margin-top", "-0.75rem"); property("margin-bottom", "1.75rem")
                } }) {
                    val dict = LocaleManager.dictionary
                    val subTabs = listOf(
                        dict.showcaseSubTabWeek to "fa-solid fa-calendar-week",
                        dict.showcaseSubTabMonth to "fa-solid fa-calendar",
                        dict.showcaseSubTabYear to "fa-solid fa-calendar-days"
                    )
                    subTabs.forEachIndexed { sIdx, (sLabel, sIcon) ->
                        Button({
                            onClick { activeSchemaSubTab = sIdx }
                            style {
                                property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.35rem")
                                property("padding", "0.35rem 0.85rem"); property("border-radius", "6px")
                                property("font-size", "0.78rem"); property("font-weight", "600"); property("font-family", "inherit")
                                property("cursor", "pointer"); property("transition", "all 0.15s")
                                property("border", if (sIdx == activeSchemaSubTab) "1px solid var(--rp-info-border)" else "1px solid var(--rp-hl-med)")
                                property("background", if (sIdx == activeSchemaSubTab) "var(--rp-info-bg)" else "var(--rp-surface)")
                                property("color", if (sIdx == activeSchemaSubTab) "var(--rp-pine)" else "var(--rp-subtle)")
                                property("white-space", "nowrap")
                            }
                        }) {
                            FaIcon(sIcon, size = "0.7rem", color = if (sIdx == activeSchemaSubTab) "var(--rp-pine)" else "var(--rp-subtle)")
                            Text(" $sLabel")
                        }
                    }
                }
            }

            // Screenshot frame
            Div({ classes("reveal"); style {
                property("border-radius", "16px"); property("overflow", "hidden")
                property("box-shadow", "0 20px 50px rgba(15, 23, 42, 0.12)")
                property("border", "1px solid var(--rp-hl-med)")
                property("background", "var(--rp-surface)")
            } }) {
                // Browser chrome bar
                Div({ style {
                    property("background", "var(--rp-overlay)"); property("padding", "10px 16px")
                    property("display", "flex"); property("align-items", "center"); property("gap", "8px")
                } }) {
                    listOf("#FF5F57", "#FFBD2E", "#28C940").forEach { color ->
                        Div({ style {
                            property("width", "12px"); property("height", "12px")
                            property("border-radius", "50%"); property("background", color)
                        } })
                    }
                    Div({ style {
                        property("flex", "1"); property("background", "var(--rp-surface)"); property("border-radius", "6px")
                        property("height", "22px"); property("display", "flex"); property("align-items", "center")
                        property("padding", "0 10px"); property("font-size", "0.7rem"); property("color", "var(--rp-muted)")
                        property("margin", "0 12px")
                    } }) { Text("app.mittpass.se") }
                }
                // Screenshot image
                tabs.forEachIndexed { idx, (_, label, src) ->
                    val finalSrc = if (idx == 0) {
                        when (activeSchemaSubTab) {
                            0 -> "screen_schema_week.png"
                            1 -> "screen_schema_month.png"
                            2 -> "screen_schema_year.png"
                            else -> "screen_schema_week.png"
                        }
                    } else src

                    Img(src = finalSrc, alt = label) {
                        style {
                            property("width", "100%"); property("display", if (idx == activeTab) "block" else "none")
                            property("vertical-align", "top")
                        }
                    }
                }
            }
        }
    }
}


// ─────────────────────────────────────────
// SIGN UP SECTION
// ─────────────────────────────────────────
@Composable
fun LandingSignUp(onSignUp: () -> Unit = {}) {
    var orgName by remember { mutableStateOf("") }
    var workplaceName by remember { mutableStateOf("") }
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var error by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(false) }
    var verifyPending by remember { mutableStateOf(false) }

    Section({
        id("skapa-konto")
        style {
            property("padding", "5rem 1.75rem")
            property("background", "var(--rp-surface)")
        }
    }) {
        Div({ style {
            property("max-width", "520px"); property("margin", "0 auto")
        } }) {
            val dict = LocaleManager.dictionary
            H2({ style {
                property("font-family", "var(--font-serif)")
                property("font-size", "clamp(1.75rem, 4vw, 2.5rem)")
                property("font-weight", "800"); property("color", "var(--rp-text)")
                property("margin", "0 0 0.5rem 0"); property("letter-spacing", "-0.02em")
                property("text-align", "center")
            } }) { Text(dict.signUpTitle) }
            P({ style {
                property("font-size", "1rem"); property("color", "var(--rp-subtle)")
                property("margin", "0 0 2rem 0"); property("line-height", "1.7")
                property("text-align", "center")
            } }) { Text(dict.signUpSub) }

            Div({ style {
                property("background", "var(--rp-surface)")
                property("border", "1.5px solid var(--rp-hl-med)")
                property("border-radius", "24px")
                property("padding", "2.25rem 2rem")
                property("box-shadow", "0 8px 40px rgba(44,63,168,0.08)")
            } }) {
                if (verifyPending) {
                    Div({ style { property("text-align", "center"); property("padding", "1rem 0") } }) {
                        Div({ style { property("margin-bottom", "1rem") } }) {
                            FaIcon("fa-solid fa-envelope-open-text", size = "2.5rem", color = "var(--rp-pine)")
                        }
                        H3({ style { property("font-size", "1.25rem"); property("font-weight", "800"); property("color", "var(--rp-text)"); property("margin", "0 0 0.75rem 0") } }) {
                            Text(dict.signUpSuccessTitle)
                        }
                        P({ style { property("color", "var(--rp-subtle)"); property("font-size", "0.95rem"); property("line-height", "1.6"); property("margin", "0") } }) {
                            Text(dict.signUpSuccessPrefix)
                            Span({ style { property("font-weight", "700"); property("color", "var(--rp-text)") } }) { Text(email) }
                            Text(dict.signUpSuccessSuffix)
                        }
                    }
                } else {
                    error?.let { err ->
                        Div({ style {
                            property("background", "var(--rp-err-bg)"); property("color", "var(--rp-love)")
                            property("padding", "0.75rem 1rem"); property("border-radius", "12px")
                            property("margin-bottom", "1.25rem"); property("font-size", "0.88rem"); property("font-weight", "600")
                        } }) { Text(err) }
                    }

                    landingField(dict.fieldNameOrgName, "org-name", InputType.Text, orgName, dict.fieldPlaceholderOrgName) { orgName = it; error = null }
                    landingField(dict.fieldNameWorkplace, "wp-name", InputType.Text, workplaceName, dict.fieldPlaceholderWorkplace) { workplaceName = it; error = null }
                    landingField(dict.fieldNameEmail, "signup-email", InputType.Email, email, dict.fieldPlaceholderEmail) { email = it; error = null }
                    landingField(dict.fieldNamePassword, "signup-password", InputType.Password, password, dict.fieldPlaceholderPassword) { password = it; error = null }

                    Div({ style { property("height", "0.25rem") } })

                    Button({
                        onClick {
                            if (orgName.isBlank() || workplaceName.isBlank() || email.isBlank() || password.isBlank()) {
                                error = dict.contactFillAllFields; return@onClick
                            }
                            if (password.length < 8) { error = dict.signUpPasswordMinLen; return@onClick }
                            loading = true; error = null
                            MainScope().launch {
                                authRepo.register(RegisterRequest(orgName.trim(), workplaceName.trim(), email.trim(), password)).fold(
                                    onSuccess = { authResponse ->
                                        if (authResponse == null) {
                                            verifyPending = true
                                            loading = false
                                            js("if(typeof gtag !== 'undefined') gtag('event', 'sign_up', { method: 'email' })")
                                        } else {
                                            js("if(typeof gtag !== 'undefined') gtag('event', 'sign_up', { method: 'email' })")
                                            onSignUp()
                                        }
                                    },
                                    onFailure = { e ->
                                        error = e.message?.take(120) ?: dict.signUpRegisterError
                                        loading = false
                                    }
                                )
                            }
                        }
                        if (loading) disabled()
                        style {
                            property("display", "block"); property("width", "100%")
                            property("padding", "0.95rem"); property("font-size", "1rem")
                            property("font-weight", "700"); property("font-family", "inherit")
                            property("background", if (loading) "var(--rp-muted)" else "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))")
                            property("color", "white"); property("border-radius", "12px"); property("border", "none")
                            property("min-height", "52px"); property("cursor", if (loading) "not-allowed" else "pointer")
                            property("box-shadow", "0 4px 20px rgba(44,63,168,0.25)")
                            property("margin-top", "1.25rem")
                            property("letter-spacing", "0.01em")
                        }
                    }) { Text(if (loading) dict.signUpCreatingAccount else dict.signUpCreateAccountBtn) }

                    P({ style {
                        property("margin-top", "1rem"); property("text-align", "center")
                        property("color", "var(--rp-muted)"); property("font-size", "0.76rem"); property("line-height", "1.5")
                    } }) {
                        Text(dict.signUpAlreadyHaveAccount)
                    }
                }
            }
        }
    }
}

@Composable
private fun landingField(label: String, id: String, type: InputType<String>, value: String, placeholder: String, onChange: (String) -> Unit) {
    Div({ style { property("margin-bottom", "1rem") } }) {
        Label(attrs = {
            attr("for", id)
            style {
                property("display", "block"); property("font-size", "0.75rem"); property("font-weight", "700")
                property("color", "var(--rp-text)"); property("text-transform", "uppercase")
                property("letter-spacing", "0.5px"); property("margin-bottom", "0.4rem")
            }
        }) { Text(label) }
        Input(type) {
            id(id); value(value); onInput { onChange(it.value) }
            attr("placeholder", placeholder)
            style {
                property("width", "100%"); property("padding", "0.8rem 1rem")
                property("border", "2px solid var(--rp-hl-med)"); property("border-radius", "12px")
                property("font-size", "0.95rem"); property("outline", "none")
                property("box-sizing", "border-box"); property("font-family", "inherit")
                property("color", "var(--rp-text)")
            }
        }
    }
}


// ─────────────────────────────────────────
// FOUNDER NOTE
// ─────────────────────────────────────────
@Composable
fun LandingFounderNote() {
    Section({ style {
        property("padding", "4rem 1.75rem")
        property("background", "var(--rp-surface)")
    } }) {
        Div({ style {
            property("max-width", "700px"); property("margin", "0 auto")
        } }) {
            val dict = LocaleManager.dictionary
            Div({ classes("founder-card", "reveal"); style {
                property("background", "var(--rp-surface)")
                property("border", "1px solid var(--rp-hl-med)")
                property("border-radius", "20px")
                property("padding", "2.5rem 2.75rem")
                property("box-shadow", "0 8px 40px rgba(44,63,168,0.07)")
                property("position", "relative")
                property("overflow", "hidden")
                property("direction", "ltr")
                property("text-align", "left")
            } }) {
                // Quote mark
                Div({ style {
                    property("position", "absolute"); property("top", "1.5rem"); property("right", "2rem")
                    property("font-size", "5rem"); property("font-family", "Georgia, serif")
                    property("color", "var(--rp-info-bg)"); property("line-height", "1"); property("pointer-events", "none")
                    property("user-select", "none")
                } }) { Text("”") }
                // Quote icon
                Div({ style {
                    property("width", "40px"); property("height", "40px"); property("border-radius", "10px")
                    property("background", "var(--rp-info-bg)")
                    property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                    property("margin-bottom", "1.25rem")
                } }) { FaIcon("fa-solid fa-quote-left", size = "1rem", color = "var(--rp-pine)") }
                // Quote text
                P({ style {
                    property("font-size", "clamp(1rem, 2vw, 1.15rem)")
                    property("color", "var(--rp-text)"); property("line-height", "1.75")
                    property("font-weight", "500"); property("margin", "0 0 1.75rem")
                } }) {
                    Text(dict.founderNoteQuote)
                }
                // Attribution row
                Div({ style {
                    property("display", "flex"); property("align-items", "center"); property("gap", "0.875rem")
                } }) {
                    Div({ style {
                        property("width", "44px"); property("height", "44px"); property("border-radius", "50%")
                        property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))")
                        property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                        property("color", "white"); property("font-weight", "800"); property("font-size", "0.9rem")
                        property("flex-shrink", "0")
                    } }) { Text("H") }
                    Div {
                        Div({ style {
                            property("font-weight", "700"); property("color", "var(--rp-text)"); property("font-size", "0.9rem")
                        } }) { Text(dict.founderName) }
                        Div({ style {
                            property("font-size", "0.78rem"); property("color", "var(--rp-muted)"); property("font-weight", "500")
                        } }) { Text(dict.founderTitle) }
                    }
                }
            }
        }
    }
}

// ─────────────────────────────────────────
// SHARED HELPERS
// ─────────────────────────────────────────
@Composable
private fun sectionLabel(text: String, light: Boolean) {
    Span({
        style {
            property("display", "inline-flex")
            property("align-items", "center")
            property("gap", "0.4rem")
            property("font-size", "0.75rem")
            property("font-weight", "700")
            property("letter-spacing", "0.07em")
            property("text-transform", "uppercase")
            property("margin-bottom", "0.875rem")
            if (light) {
                property("color", "rgba(120,140,230,0.9)")
            } else {
                property("color", "var(--rp-pine)")
            }
        }
    }) {
        Span({
            style {
                property("width", "18px")
                property("height", "2px")
                property("border-radius", "2px")
                property("display", "inline-block")
                if (light) property("background", "rgba(120,140,230,0.7)") else property("background", "var(--rp-pine)")
            }
        })
        Text(text)
    }
}
